/**
 * Mock data for the Lighthouse Review page.
 * Used by LighthouseApi.getReview() in dev mode to render a realistic OPF review.
 */
import {LighthouseReviewData} from 'src/app/api/model/response/LighthouseReviewResponse';
import {FieldType, ReviewStatus, ReviewType} from 'src/app/constant/Enums';

export const MOCK_LIGHTHOUSE_REVIEW_DATA: LighthouseReviewData = {
    reviewId: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
    referenceId: 'A1X1BOUT4WLDV3',
    referenceSubType: 'DP',
    referenceType: 'TRANSPORTER',
    caseType: 'BWND',
    caseSubType: 'BWND',
    businessType: 'AMZL',
    reviewType: ReviewType.PRIMARY_AUDIT,
    reviewStatus: ReviewStatus.PENDING,
    reviewResult: '',
    country: 'US',
    reOpenFor: null,
    creationDate: 1719828000000,
    lastModifiedBy: null,
    lastModifiedDate: 1719828000000,
    lastReviewDecisionDetail: null,

    caseDetails: [
        {label: 'Transporter ID', value: 'A252AB3GLGSDBQ (ACTIVE)', type: FieldType.LINK, href: 'https://logistics.amazon.com/amconsole/transporter/A252AB3GLGSDBQ'},
        {label: 'Case Creation Date', value: '2026-07-21 12:59:07 UTC', type: FieldType.TEXT},
        {label: 'Case Status', value: 'PENDING', type: FieldType.TEXT},
        {label: 'Investigation Date', value: 'N/A', type: FieldType.TEXT},
        {label: 'Sub use case', value: 'OPF', type: FieldType.TEXT},
        {label: 'Human in loop decision', value: 'PENDING', type: FieldType.TEXT},
        {label: 'Action Result', value: 'N/A', type: FieldType.TEXT},
        {label: 'Blurb Sent to the DP', value: 'N/A', type: FieldType.TEXT}
    ],

    auditChecks: [
        {label: 'Is this a duplicate ticket?', value: null, type: FieldType.RADIO, options: ['Yes', 'No'], editable: true, required: true},
        {label: 'Has this DP been previously reinstated?', value: null, type: FieldType.RADIO, options: ['Yes', 'No'], editable: true, required: true},
        {label: "Are there applicable SYSKA's on DPs account?", value: null, type: FieldType.RADIO, options: ['Yes', 'No'], editable: true, required: true},
        {label: 'Is the DP listed on the DND List?', value: 'No', type: FieldType.TEXT, editable: false},
        {label: 'Does the DP have an active High-Severity ticket?', value: null, type: FieldType.RADIO, options: ['Yes', 'No'], editable: true, required: true},
        {label: "Is the DP impacted by LSE's?", value: 'No', type: FieldType.TEXT, editable: false},
        {label: "Number of LSE's", value: '0', type: FieldType.NUMBER, editable: false},
        {label: 'Number of Blocks Forgiven', value: '0', type: FieldType.NUMBER, editable: false},
        {label: "Applicable LSE's", value: null, type: FieldType.DROPDOWN, options: ['None', 'LSE-2026-001', 'LSE-2026-002', 'LSE-2026-003'], editable: true},
        {label: 'Exemption reason', value: null, type: FieldType.FREEFORM, editable: true},
        {label: 'New Block total in 90 days Lookback', value: '39', type: FieldType.NUMBER, editable: false},
        {label: 'New BWND total in 90 days Lookback', value: '24', type: FieldType.NUMBER, editable: false},
        {label: 'New Threshold Abuse 90 days Lookback BWND rate', value: '61.54 %', type: FieldType.TEXT, editable: false},
        {label: 'Avg check-in to DL scan time', value: '13.67 mins', type: FieldType.TEXT, editable: false},
        {label: 'Avg station wave BWND', value: '53.60 %', type: FieldType.TEXT, editable: false},
        {label: 'Avg block start to swipe to arrive', value: '-4.38', type: FieldType.NUMBER, editable: false},
        {label: 'Number of alerts sent', value: '2', type: FieldType.NUMBER, editable: false},
        {label: 'Last alert sent', value: '2026-07-19 12:45:24.86', type: FieldType.TEXT, editable: false},
        {label: 'No of defect post last alert', value: '4', type: FieldType.NUMBER, editable: false},
        {label: 'Business Date', value: '2026-07-20T18:00:00.000Z', type: FieldType.TEXT, editable: false},
        {label: 'Is Offboarding ticket filled by authorized person?', value: null, type: FieldType.RADIO, options: ['Yes', 'No'], editable: true, required: true},
        {label: 'Is DP still active?', value: null, type: FieldType.RADIO, options: ['Yes', 'No'], editable: true, required: true},
        {label: 'Is the Offboarding ticket Fraud-related?', value: null, type: FieldType.RADIO, options: ['Yes', 'No'], editable: true, required: true}
    ],

    dataPoints: [
        {label: 'Transporter ID', value: 'A252AB3GLGSDBQ', type: FieldType.TEXT},
        {label: 'Country', value: 'US', type: FieldType.TEXT},
        {label: 'Region', value: 'Greater Seattle Area', type: FieldType.TEXT},
        {label: 'OPF Threshold Met', value: 'Abuse', type: FieldType.TEXT},
        {label: 'Lookback Period', value: '90 days', type: FieldType.TEXT},
        {label: 'BWNDs in 90 days lookback', value: '24', type: FieldType.NUMBER},
        {label: 'Blocks in 90 days lookback', value: '39', type: FieldType.NUMBER},
        {label: 'BWND Rate in 90 days lookback', value: '61.54 %', type: FieldType.TEXT},
        {label: 'OPF Probability Score', value: '0.0005', type: FieldType.NUMBER},
        {label: 'Business', value: 'SSD', type: FieldType.TEXT}
    ],

    history: {
        auditTrail: [
            {
                auditor: 'arjunaju',
                action: 'DECISION_OFFBOARD',
                timestamp: 1753004423000,
                notes: 'Primary auditor verdict: DP is eligible for offboarding after secondary review.',
                auditCheckResponses: {
                    'Is this a duplicate ticket?': 'No',
                    'Has this DP been previously reinstated?': 'No',
                    'Is the DP listed on the DND List?': 'No',
                    'Does the DP have an active High-Severity ticket?': 'No',
                    'Is DP still active?': 'Yes',
                    'Is the Offboarding ticket Fraud-related?': 'Yes'
                }
            },
            {
                auditor: 'anirumit',
                action: 'DECISION_OFFBOARD',
                timestamp: 1752989631000,
                notes: 'Offboard Reason: Overbooking Payment Fraud (OPF). Blurb: amazon_flex_sls_bwnd_termination. Investigation needed: No.',
                auditCheckResponses: {
                    'Is this a duplicate ticket?': 'No',
                    'Are there applicable SYSKAs on DPs account?': 'No',
                    'Is Offboarding ticket filled by authorized person?': 'Yes',
                    'Was DP alerted twice?': 'Yes',
                    'Is the Offboarding ticket Fraud-related?': 'Yes'
                }
            },
            {
                auditor: 'raunaksg',
                action: 'COMMENT',
                timestamp: 1752980000000,
                notes: 'Adding a remark for context — no decision taken.'
            }
        ],
        attachments: [
            {fileId: 'att-uuid-001', fileName: 'evidence-screenshot.png', fileSize: 329059, uploadedBy: 'arjunaju', uploadedAt: 1753004423000},
            {fileId: 'att-uuid-002', fileName: 'evidence-screenshot.png', fileSize: 329059, uploadedBy: 'anirumit', uploadedAt: 1752989631000}
        ]
    },

    decisions: ['NO_ACTION', 'OFFBOARDED'],
    additionalTabs: []
};

/** Y/N radio audit check, matching the shape the ODQ DDB config produces. */
const ynCheck = (label: string) => ({
    label, value: null, type: FieldType.RADIO,
    options: ['Y', 'N'], editable: true, required: false
});

const numCheck = (label: string) => ({
    label, value: null, type: FieldType.NUMBER, editable: true, required: false
});

const freeCheck = (label: string) => ({
    label, value: null, type: FieldType.FREEFORM, editable: true, required: false
});

const textCheck = (label: string) => ({
    label, value: null, type: FieldType.TEXT, editable: true, required: false
});

const ODQ_TRAIL_REMARKS = [
    'Checked station delays — none relevant for the defect window.',
    'Pulled SCC report for the DP; no non-compliant scans in the lookback.',
    'Confirmed the DP is not on the DND list.'
];

/** Shared history + case-detail scaffolding for the two ODQ-family mocks. */
const ODQ_FAMILY_HISTORY = {
    auditTrail: [
        {
            auditor: 'ewm',
            action: 'CREATED',
            timestamp: 1790000000000,
            notes: 'Case was created'
        },
        // Spaced an hour apart so the rendered timestamps are all distinct.
        ...ODQ_TRAIL_REMARKS.map((notes, i) => ({
            auditor: i % 3 === 0 ? 'anayush' : (i % 3 === 1 ? 'raunaksg' : 'nvinchnd'),
            action: 'COMMENT',
            timestamp: 1790003600000 + (i * 3600000),
            notes
        }))
    ],
    attachments: [
        {
            fileId: 'att-odq-001',
            fileName: 'scc-screenshot.png',
            fileSize: 184320,
            uploadedBy: 'anayush',
            uploadedAt: 1790003600000
        }
    ]
};

/**
 * Mock ODQ review, served for any reviewId containing `lh-odq` on the local dev server.
 * Mirrors the ODQ DDB config: link-typed Transporter ID plus the 17-item checklist.
 */
export const MOCK_LIGHTHOUSE_ODQ_REVIEW_DATA: LighthouseReviewData = {
    reviewId: 'lh-odq-0001',
    referenceId: 'A3RYYY8QDIVWRF',
    referenceSubType: 'DP',
    referenceType: 'TRANSPORTER',
    caseType: 'Standings',
    caseSubType: 'ODQ',
    businessType: 'AMZL',
    reviewType: ReviewType.PRIMARY_AUDIT,
    reviewStatus: ReviewStatus.PENDING,
    reviewResult: '',
    country: 'US',
    reOpenFor: null,
    creationDate: 1790000000000,
    lastModifiedBy: null,
    lastModifiedDate: 1790000000000,
    lastReviewDecisionDetail: null,

    caseDetails: [
        {
            label: 'Transporter ID', value: 'A3RYYY8QDIVWRF', type: FieldType.LINK,
            href: 'https://logistics.amazon.com/amconsole/transporter/A3RYYY8QDIVWRF'
        },
        {label: 'Country', value: 'US', type: FieldType.TEXT},
        {label: 'Operational Status', value: 'ACTIVE', type: FieldType.TEXT}
        // Alert Count omitted on purpose — it is config-only (see ODQ/CASE_DETAILS in DDB).
    ],

    auditChecks: [
        ynCheck('1) Is Driver over the package cap'),
        ynCheck('2) Any relevant LSE tickets found'),
        ynCheck('3) Any relevant station delays found'),
        ynCheck('4a) Planned delivery outside of business hours'),
        ynCheck('4b) Any Secure Location scans found'),
        ynCheck('4c) Any Customer unavailable instance'),
        ynCheck('5) Any Access/Locker Issues found'),
        ynCheck('6) Any valid LHE conditions'),
        freeCheck('7) Any older or other defect Exempted'),
        ynCheck('8a) DNR Defects adjudicated'),
        numCheck('8b) Initial number of DNR defects'),
        numCheck('8c) Total defects after removing Amazon and customer responsible DNRs'),
        ynCheck('8d) Exemptions granted, if any?'),
        freeCheck('8e) Reasons for exemptions'),
        ynCheck("Sanity 1) Are there applicable SYSKA's on DPs account?"),
        ynCheck('Sanity 2) Is the DP listed on the DND List?'),
        ynCheck('Sanity 3) Does the DP have an active High-Severity ticket?')
    ],

    // ODQ shows the defect table in place of Data Points, so this stays empty.
    dataPoints: [],

    history: ODQ_FAMILY_HISTORY,

    decisions: ['NO_ACTION', 'OFFBOARDED'],
    reasonCodes: ['SERVICE_LEVEL_STANDARDS_VIOLATION'],
    rootCauses: [
        'Offboarding - Overall Delivery Quality',
        'Offboarding - RTS Egregious',
        'Non SSR Offboarding'
    ],
    additionalTabs: [],

    pdSnapshot: {
        standingName: 'OVERALL_DELIVERY_QUALITY',
        totalDefects: 2,
        totalOpportunitiesScored: 500,
        defectRatePct: 0.4,
        uniqueDefectDays: 1,
        regionId: '1',
        defects: [
            {
                opportunityId: 'tr-cw-dnk5-ead0211c-ba57-4781-04eb-796af2881ae1',
                defectType: 'UNRETURNED_PACKAGE',
                defectDate: '2026-08-28T00:00:00.000Z',
                enriched: true,
                enrichedFields: {
                    tracking_id: 'TBA334085487251',
                    station_code: 'DNK5',
                    delivery_window_start_date_time_in_iso: '2026-08-28T04:01:00Z',
                    delivery_window_end_date_time_in_iso: '2026-08-29T01:00:00Z'
                }
            },
            {
                opportunityId: 'tr-cw-dnk5-bcd02859-8894-d859-9b1a-d7d27b9c9f63',
                defectType: 'UNDELIVERED_PACKAGE',
                defectDate: '2026-08-31T00:00:00.000Z',
                enriched: true,
                enrichedFields: {
                    tracking_id: 'TBA334164428023',
                    station_code: 'DNK5',
                    delivery_window_start_date_time_in_iso: '2026-08-31T04:01:00Z',
                    delivery_window_end_date_time_in_iso: '2026-09-01T01:00:00Z'
                }
            }
        ]
    }
};

/**
 * Mock Reliability review, served for any reviewId containing `lh-rel` on the dev server.
 * Same layout as ODQ; differs in its checklist and in carrying block-level defects.
 */
export const MOCK_LIGHTHOUSE_RELIABILITY_REVIEW_DATA: LighthouseReviewData = {
    ...MOCK_LIGHTHOUSE_ODQ_REVIEW_DATA,
    reviewId: 'lh-rel-0001',
    caseSubType: 'Reliability',

    auditChecks: [
        ynCheck('a) Is DP part of DND List?'),
        ynCheck("b) Is DP's standing Unacceptable?"),
        ynCheck('c) Does DP meet recent work history criteria?'),
        ynCheck('d) Has DP received mail alerts sent for 2 unique defects?'),
        ynCheck("e) Is there any SYSKA present in DP's account?"),
        textCheck('f) Exemptions granted'),
        textCheck('g) Exemption category'),
        textCheck('h) Exemption Contact ID'),
        textCheck('i) Exempted block'),
        textCheck('j) Closure Code'),
        textCheck('k) DP Standing post audit')
    ],

    // rootCause is mandatory for ODQ only, so Reliability offers none.
    rootCauses: [],

    pdSnapshot: {
        standingName: 'RELIABILITY',
        totalDefects: 2,
        totalOpportunitiesScored: 120,
        defectRatePct: 1.7,
        uniqueDefectDays: 2,
        regionId: '1',
        defects: [
            {
                opportunityId: 'br-cw-dnk5-11a0211c-ba57-4781-04eb-796af2881ae1',
                defectType: 'NO_SHOW',
                defectDate: '2026-08-28T00:00:00.000Z',
                enriched: true,
                enrichedFields: {
                    station_code: 'DNK5',
                    shift_start_date_time_in_iso: '2026-08-28T04:00:00Z',
                    shift_end_date_time_in_iso: '2026-08-28T08:00:00Z'
                }
            },
            {
                opportunityId: 'br-cw-dnk5-22b02246-a31c-9f41-b1ab-660c9e33c2e1',
                defectType: 'LATE_CANCEL',
                defectDate: '2026-08-30T00:00:00.000Z',
                enriched: true,
                enrichedFields: {
                    station_code: 'DNK5',
                    shift_start_date_time_in_iso: '2026-08-30T12:00:00Z',
                    shift_end_date_time_in_iso: '2026-08-30T16:00:00Z'
                }
            }
        ]
    }
};
